import FunnelsAPI from 'dashboard/api/funnels';
import FunnelItemsAPI from 'dashboard/api/funnelItems';

const state = {
  records: [],
  currentFunnel: null,
  stages: [],
  items: {},
  uiFlags: {
    isFetching: false,
    isFetchingStages: false,
    isFetchingItems: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getFunnels: _state => _state.records,
  getCurrentFunnel: _state => _state.currentFunnel,
  getStages: _state => _state.stages,
  getItemsByStage: _state => stageId => _state.items[stageId] || [],
  getAllItems: _state => Object.values(_state.items).flat(),
  getUIFlags: _state => _state.uiFlags,
  getFunnelById: _state => id => _state.records.find(f => f.id === Number(id)),
};

const mutations = {
  SET_FUNNELS(_state, funnels) {
    _state.records = funnels;
  },
  SET_CURRENT_FUNNEL(_state, funnel) {
    _state.currentFunnel = funnel;
  },
  ADD_FUNNEL(_state, funnel) {
    _state.records.push(funnel);
  },
  UPDATE_FUNNEL(_state, funnel) {
    const index = _state.records.findIndex(f => f.id === funnel.id);
    if (index !== -1) {
      _state.records.splice(index, 1, funnel);
    }
    if (_state.currentFunnel?.id === funnel.id) {
      _state.currentFunnel = funnel;
    }
  },
  DELETE_FUNNEL(_state, id) {
    _state.records = _state.records.filter(f => f.id !== id);
    if (_state.currentFunnel?.id === id) {
      _state.currentFunnel = null;
    }
  },
  SET_STAGES(_state, stages) {
    _state.stages = stages;
  },
  ADD_STAGE(_state, stage) {
    _state.stages.push(stage);
    _state.items[stage.id] = [];
  },
  UPDATE_STAGE(_state, stage) {
    const index = _state.stages.findIndex(s => s.id === stage.id);
    if (index !== -1) {
      _state.stages.splice(index, 1, stage);
    }
  },
  DELETE_STAGE(_state, stageId) {
    _state.stages = _state.stages.filter(s => s.id !== stageId);
    delete _state.items[stageId];
  },
  SET_ITEMS(_state, { stageId, items }) {
    _state.items = { ..._state.items, [stageId]: items };
  },
  SET_ALL_ITEMS(_state, itemsByStage) {
    _state.items = itemsByStage;
  },
  ADD_ITEM(_state, item) {
    const stageId = item.funnel_stage_id;
    if (!_state.items[stageId]) {
      _state.items[stageId] = [];
    }
    _state.items[stageId].push(item);
  },
  UPDATE_ITEM(_state, item) {
    const stageId = item.funnel_stage_id;
    if (_state.items[stageId]) {
      const index = _state.items[stageId].findIndex(i => i.id === item.id);
      if (index !== -1) {
        _state.items[stageId].splice(index, 1, item);
      }
    }
  },
  DELETE_ITEM(_state, { stageId, itemId }) {
    if (_state.items[stageId]) {
      _state.items[stageId] = _state.items[stageId].filter(
        i => i.id !== itemId
      );
    }
  },
  MOVE_ITEM(_state, { item, fromStageId, toStageId }) {
    // Remove from old stage
    if (_state.items[fromStageId]) {
      _state.items[fromStageId] = _state.items[fromStageId].filter(
        i => i.id !== item.id
      );
    }
    // Add to new stage
    if (!_state.items[toStageId]) {
      _state.items[toStageId] = [];
    }
    _state.items[toStageId].push({ ...item, funnel_stage_id: toStageId });
    // Sort by position
    _state.items[toStageId].sort((a, b) => a.position - b.position);
  },
  REORDER_ITEM(_state, { stageId, itemId, newPosition }) {
    if (!_state.items[stageId]) return;

    const items = [..._state.items[stageId]];
    const itemIndex = items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;

    // Remove item from current position
    const [item] = items.splice(itemIndex, 1);

    // Insert at new position
    items.splice(newPosition, 0, item);

    // Update positions for all items
    items.forEach((i, idx) => {
      i.position = idx;
    });

    _state.items[stageId] = items;
  },
  SET_UI_FLAG(_state, { key, value }) {
    _state.uiFlags[key] = value;
  },
};

const actions = {
  async fetchFunnels({ commit }) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await FunnelsAPI.get();
      commit('SET_FUNNELS', data);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async fetchFunnel({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await FunnelsAPI.show(id);
      commit('SET_CURRENT_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async createFunnel({ commit }, funnelData) {
    commit('SET_UI_FLAG', { key: 'isCreating', value: true });
    try {
      const { data } = await FunnelsAPI.create(funnelData);
      commit('ADD_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isCreating', value: false });
    }
  },

  async updateFunnel({ commit }, { id, ...funnelData }) {
    commit('SET_UI_FLAG', { key: 'isUpdating', value: true });
    try {
      const { data } = await FunnelsAPI.update(id, funnelData);
      commit('UPDATE_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isUpdating', value: false });
    }
  },

  async deleteFunnel({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isDeleting', value: true });
    try {
      await FunnelsAPI.delete(id);
      commit('DELETE_FUNNEL', id);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isDeleting', value: false });
    }
  },

  // Stages
  async fetchStages({ commit }, funnelId) {
    commit('SET_UI_FLAG', { key: 'isFetchingStages', value: true });
    try {
      const { data } = await FunnelsAPI.getStages(funnelId);
      commit('SET_STAGES', data);
      // Initialize items for each stage
      const itemsByStage = {};
      data.forEach(stage => {
        itemsByStage[stage.id] = [];
      });
      commit('SET_ALL_ITEMS', itemsByStage);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetchingStages', value: false });
    }
  },

  async createStage({ commit }, { funnelId, ...stageData }) {
    try {
      const { data } = await FunnelsAPI.createStage(funnelId, stageData);
      commit('ADD_STAGE', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateStage({ commit }, { funnelId, stageId, ...stageData }) {
    try {
      const { data } = await FunnelsAPI.updateStage(
        funnelId,
        stageId,
        stageData
      );
      commit('UPDATE_STAGE', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteStage({ commit }, { funnelId, stageId }) {
    try {
      await FunnelsAPI.deleteStage(funnelId, stageId);
      commit('DELETE_STAGE', stageId);
    } catch (error) {
      throw error;
    }
  },

  async reorderStages({ commit }, { funnelId, stageIds }) {
    try {
      const { data } = await FunnelsAPI.reorderStages(funnelId, stageIds);
      commit('SET_STAGES', data);
    } catch (error) {
      throw error;
    }
  },

  // Items
  async fetchItems(
    { commit },
    { funnelId, stageId, dateFrom, dateTo, allDates }
  ) {
    commit('SET_UI_FLAG', { key: 'isFetchingItems', value: true });
    try {
      const params = { stage_id: stageId };

      // Pass date filters to backend for better performance
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (allDates) params.all_dates = true;

      const { data } = await FunnelsAPI.getItems(funnelId, params);
      if (stageId) {
        commit('SET_ITEMS', { stageId, items: data });
      } else {
        // Group items by stage
        const itemsByStage = {};
        data.forEach(item => {
          if (!itemsByStage[item.funnel_stage_id]) {
            itemsByStage[item.funnel_stage_id] = [];
          }
          itemsByStage[item.funnel_stage_id].push(item);
        });
        commit('SET_ALL_ITEMS', itemsByStage);
      }
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetchingItems', value: false });
    }
  },

  async createItem({ commit }, { funnelId, ...itemData }) {
    try {
      const { data } = await FunnelsAPI.createItem(funnelId, itemData);
      commit('ADD_ITEM', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateItem({ commit }, { funnelId, itemId, ...itemData }) {
    try {
      const { data } = await FunnelsAPI.updateItem(funnelId, itemId, itemData);
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteItem({ commit }, { funnelId, stageId, itemId }) {
    try {
      await FunnelsAPI.deleteItem(funnelId, itemId);
      commit('DELETE_ITEM', { stageId, itemId });
    } catch (error) {
      throw error;
    }
  },

  async moveItem(
    { commit, state: _state },
    { funnelId, itemId, fromStageId, toStageId, position }
  ) {
    const item = _state.items[fromStageId]?.find(i => i.id === itemId);
    if (!item) return;

    // Optimistic update
    commit('MOVE_ITEM', { item, fromStageId, toStageId });

    try {
      const { data } = await FunnelsAPI.moveItem(
        funnelId,
        itemId,
        toStageId,
        position
      );
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      // Rollback on error
      commit('MOVE_ITEM', {
        item: { ...item, funnel_stage_id: fromStageId },
        fromStageId: toStageId,
        toStageId: fromStageId,
      });
      throw error;
    }
  },

  async reorderItem(
    { commit, state: _state },
    { funnelId, itemId, stageId, position }
  ) {
    const items = _state.items[stageId] || [];
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const oldPosition = item.position;

    // Optimistic update
    commit('REORDER_ITEM', { stageId, itemId, newPosition: position });

    try {
      const { data } = await FunnelsAPI.moveItem(
        funnelId,
        itemId,
        stageId,
        position
      );
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      // Rollback on error
      commit('REORDER_ITEM', { stageId, itemId, newPosition: oldPosition });
      throw error;
    }
  },

  // Nested resources (using FunnelItemsAPI)
  async fetchItemChecklists(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getChecklists(accountId, itemId);
    return data;
  },

  async createItemChecklist(_, { accountId, itemId, ...checklistData }) {
    const { data } = await FunnelItemsAPI.createChecklist(
      accountId,
      itemId,
      checklistData
    );
    return data;
  },

  async toggleItemChecklist(_, { accountId, itemId, checklistId }) {
    const { data } = await FunnelItemsAPI.toggleChecklist(
      accountId,
      itemId,
      checklistId
    );
    return data;
  },

  async deleteItemChecklist(_, { accountId, itemId, checklistId }) {
    await FunnelItemsAPI.deleteChecklist(accountId, itemId, checklistId);
  },

  async fetchItemNotes(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getNotes(accountId, itemId);
    return data;
  },

  async createItemNote(_, { accountId, itemId, content }) {
    const { data } = await FunnelItemsAPI.createNote(
      accountId,
      itemId,
      content
    );
    return data;
  },

  async deleteItemNote(_, { accountId, itemId, noteId }) {
    await FunnelItemsAPI.deleteNote(accountId, itemId, noteId);
  },

  async fetchItemParticipants(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getParticipants(accountId, itemId);
    return data;
  },

  async addItemParticipant(_, { accountId, itemId, userId }) {
    const { data } = await FunnelItemsAPI.addParticipant(
      accountId,
      itemId,
      userId
    );
    return data;
  },

  async removeItemParticipant(_, { accountId, itemId, participantId }) {
    await FunnelItemsAPI.removeParticipant(accountId, itemId, participantId);
  },

  async fetchItemLabels(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getLabels(accountId, itemId);
    return data;
  },

  async addItemLabel(_, { accountId, itemId, labelId }) {
    const { data } = await FunnelItemsAPI.addLabel(accountId, itemId, labelId);
    return data;
  },

  async removeItemLabel(_, { accountId, itemId, labelId }) {
    await FunnelItemsAPI.removeLabel(accountId, itemId, labelId);
  },

  async fetchItemActivities(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getActivities(accountId, itemId);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
